import {
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactElement,
} from "react";
import { Search, UserCheck, UserX, X } from "lucide-react";
import { Stack, Surface, Text, TextInput } from "@ux/primitives";
import { palette, radius, space, typography } from "@ux/tokens";
import { useUserDirectorySearch } from "../../admin/users/hooks";
import type { UserDirectoryEntry } from "../../admin/users/types";

export interface UserPickerProps {
    readonly id?: string;
    readonly value: UserDirectoryEntry | null;
    readonly onChange: (user: UserDirectoryEntry | null) => void;
    readonly placeholder?: string;
    readonly disabled?: boolean;
    readonly invalid?: boolean;
    readonly autoFocus?: boolean;
}

/**
 * Typeahead user picker backed by the Office user-search proxy.
 *
 * Two visual states:
 *   - empty   → text input + dropdown of HR matches
 *   - picked  → compact summary card with a clear button
 *
 * The dropdown closes on outside-click. List items use mousedown rather
 * than click so selection registers before the input's blur cancels the
 * popup.
 */
export function UserPicker({
    id,
    value,
    onChange,
    placeholder = "Search by name or email…",
    disabled = false,
    invalid = false,
    autoFocus = false,
}: UserPickerProps): ReactElement {
    if (value !== null) {
        return (
            <SelectedUserCard
                user={value}
                onClear={() => onChange(null)}
                disabled={disabled}
            />
        );
    }

    return (
        <UserSearchField
            id={id}
            placeholder={placeholder}
            disabled={disabled}
            invalid={invalid}
            autoFocus={autoFocus}
            onPick={onChange}
        />
    );
}

interface UserSearchFieldProps {
    readonly id: string | undefined;
    readonly placeholder: string;
    readonly disabled: boolean;
    readonly invalid: boolean;
    readonly autoFocus: boolean;
    readonly onPick: (user: UserDirectoryEntry) => void;
}

function UserSearchField({
    id,
    placeholder,
    disabled,
    invalid,
    autoFocus,
    onPick,
}: UserSearchFieldProps): ReactElement {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const search = useUserDirectorySearch(query);
    const trimmed = query.trim();
    const minMet = trimmed.length >= 2;

    useEffect(() => {
        function handleDocumentMouseDown(event: MouseEvent): void {
            if (containerRef.current === null) return;
            if (event.target instanceof Node && containerRef.current.contains(event.target)) {
                return;
            }
            setOpen(false);
        }
        document.addEventListener("mousedown", handleDocumentMouseDown);
        return () => document.removeEventListener("mousedown", handleDocumentMouseDown);
    }, []);

    const showDropdown = open && minMet;
    const isFetching = search.isFetching && minMet;
    const results = search.data ?? [];

    return (
        <div ref={containerRef} style={{ position: "relative" }}>
            <Stack direction="row" gap={2} align="center">
                <Search size={14} style={{ opacity: 0.55, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                    <TextInput
                        id={id}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        disabled={disabled}
                        invalid={invalid}
                        autoFocus={autoFocus}
                        autoComplete="off"
                    />
                </div>
            </Stack>

            {showDropdown ? (
                <UserResultsDropdown
                    isFetching={isFetching}
                    isError={search.isError}
                    results={results}
                    onPick={(user) => {
                        onPick(user);
                        setOpen(false);
                        setQuery("");
                    }}
                />
            ) : null}

            {!minMet && query.length > 0 ? (
                <Text role="muted" style={{ marginTop: space[1] }}>
                    Type at least 2 characters.
                </Text>
            ) : null}
        </div>
    );
}

interface UserResultsDropdownProps {
    readonly isFetching: boolean;
    readonly isError: boolean;
    readonly results: readonly UserDirectoryEntry[];
    readonly onPick: (user: UserDirectoryEntry) => void;
}

function UserResultsDropdown({
    isFetching,
    isError,
    results,
    onPick,
}: UserResultsDropdownProps): ReactElement {
    const containerStyle: CSSProperties = {
        position: "absolute",
        top: "calc(100% + 4px)",
        left: 0,
        right: 0,
        zIndex: 40,
        background: palette.canvas,
        border: `1px solid ${palette.border}`,
        borderRadius: radius.md,
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.45)",
        maxHeight: 320,
        overflowY: "auto",
    };

    if (isError) {
        return (
            <div style={containerStyle}>
                <Stack direction="row" gap={2} align="center" style={{ padding: space[3] }}>
                    <UserX size={16} style={{ color: palette.danger }} />
                    <Text role="muted">Search failed. Try again.</Text>
                </Stack>
            </div>
        );
    }

    if (isFetching && results.length === 0) {
        return (
            <div style={containerStyle}>
                <div style={{ padding: space[3] }}>
                    <Text role="muted">Searching…</Text>
                </div>
            </div>
        );
    }

    if (results.length === 0) {
        return (
            <div style={containerStyle}>
                <div style={{ padding: space[3] }}>
                    <Text role="muted">No matches.</Text>
                </div>
            </div>
        );
    }

    return (
        <div style={containerStyle} role="listbox">
            {results.map((user) => (
                <UserResultRow key={user.id} user={user} onPick={onPick} />
            ))}
        </div>
    );
}

interface UserResultRowProps {
    readonly user: UserDirectoryEntry;
    readonly onPick: (user: UserDirectoryEntry) => void;
}

function UserResultRow({ user, onPick }: UserResultRowProps): ReactElement {
    const [hover, setHover] = useState(false);

    const rowStyle: CSSProperties = {
        padding: `${space[2]}px ${space[3]}px`,
        cursor: "pointer",
        background: hover ? palette.surfaceRaised : "transparent",
        borderBottom: `1px solid ${palette.border}`,
    };

    const primary = formatPrimary(user);
    const secondary = formatSecondary(user);

    return (
        <div
            role="option"
            aria-selected={false}
            style={rowStyle}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            // mousedown fires before the input's blur, so the click registers
            // before the dropdown closes itself.
            onMouseDown={(e) => {
                e.preventDefault();
                onPick(user);
            }}
        >
            <Stack gap={1}>
                <Text role="body" weight="semibold">
                    {primary}
                </Text>
                {secondary !== null ? <Text role="muted">{secondary}</Text> : null}
                <Text
                    role="mono"
                    style={{ fontSize: typography.size.xs, color: palette.fgSubtle }}
                >
                    {user.id}
                </Text>
            </Stack>
        </div>
    );
}

interface SelectedUserCardProps {
    readonly user: UserDirectoryEntry;
    readonly onClear: () => void;
    readonly disabled: boolean;
}

function SelectedUserCard({
    user,
    onClear,
    disabled,
}: SelectedUserCardProps): ReactElement {
    const primary = formatPrimary(user);
    const secondary = formatSecondary(user);

    const clearStyle: CSSProperties = {
        background: "transparent",
        border: "none",
        color: palette.fgMuted,
        cursor: disabled ? "not-allowed" : "pointer",
        padding: space[1],
        borderRadius: radius.sm,
        display: "flex",
        alignItems: "center",
        gap: space[1],
    };

    return (
        <Surface tone="raised" padding={3}>
            <Stack direction="row" gap={3} align="center" justify="space-between">
                <Stack direction="row" gap={2} align="center">
                    <UserCheck size={18} style={{ color: palette.success }} />
                    <Stack gap={1}>
                        <Text role="body" weight="semibold">
                            {primary}
                        </Text>
                        {secondary !== null ? <Text role="muted">{secondary}</Text> : null}
                        <Text
                            role="mono"
                            style={{ fontSize: typography.size.xs, color: palette.fgSubtle }}
                        >
                            {user.id}
                        </Text>
                    </Stack>
                </Stack>
                <button
                    type="button"
                    onClick={onClear}
                    disabled={disabled}
                    style={clearStyle}
                    aria-label="Change user"
                >
                    <X size={14} />
                    <span style={{ fontSize: typography.size.sm }}>Change</span>
                </button>
            </Stack>
        </Surface>
    );
}

function formatPrimary(user: UserDirectoryEntry): string {
    if (user.displayName !== null && user.displayName !== undefined && user.displayName !== "") {
        return user.displayName;
    }
    if (user.email !== null && user.email !== undefined && user.email !== "") {
        return user.email;
    }
    return user.id;
}

function formatSecondary(user: UserDirectoryEntry): string | null {
    const hasDisplay =
        user.displayName !== null && user.displayName !== undefined && user.displayName !== "";
    if (
        hasDisplay &&
        user.email !== null &&
        user.email !== undefined &&
        user.email !== ""
    ) {
        return user.email;
    }
    if (
        hasDisplay &&
        user.userPrincipalName !== null &&
        user.userPrincipalName !== undefined &&
        user.userPrincipalName !== ""
    ) {
        return user.userPrincipalName;
    }
    return null;
}
